import type { Transaction } from '@/features/transactions/types'
import type { BudgetMonth, CategoryGroup } from '@/features/budget/types'

/**
 * Pure, dataset-only aggregations for the Reports screen.
 *
 * Why client-side: we already cache the full transaction list via
 * useTransactions(). Adding server-side aggregation endpoints would
 * double the maintenance for what's effectively a sum-by-key in JS.
 * Once the dataset gets too large for the client (10k+ txns), move
 * these to a backend `/reports/...` route — the shape of the result
 * is what UIs consume, so the migration is local.
 *
 * Cross-platform note: iOS already has a `ReportsManager`; the iOS
 * math currently isn't shared with web. WEB-63 (Zod → Swift codegen)
 * could eventually port these as a shared lib. For now, both sides
 * compute independently — track drift via parity ledger.
 */

export interface CategorySliceRow {
  categoryId: string | null
  categoryName: string
  groupName: string
  total: number
  pct: number
}

interface MonthlyRow {
  /** YYYY-MM, e.g. "2026-06" — keyed for chart ordering. */
  key: string
  /** "Jun 2026" — for axis labels. */
  label: string
  income: number
  expenses: number
  net: number
}

const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' })

function categoryLookup(month: BudgetMonth | undefined) {
  const byId = new Map<string, { name: string; groupName: string }>()
  if (!month) return byId
  for (const g of month.categoryGroups) {
    for (const c of g.categories) {
      byId.set(c.id, { name: c.name, groupName: g.name })
    }
  }
  return byId
}

/**
 * Sum outflows by category for the given date window. Inflows are
 * excluded (those are income, not spending). Transfers + credit-card
 * payments are excluded — they're inter-account moves, not spending.
 */
export function spendingByCategory(
  transactions: Transaction[],
  month: BudgetMonth | undefined,
  range: { startISO: string; endISO: string },
  groups: CategoryGroup[],
): CategorySliceRow[] {
  const byId = categoryLookup(month)
  const totals = new Map<string | null, number>()
  for (const t of transactions) {
    if (t.isTransfer || t.isCreditCardPayment) continue
    if (t.amount >= 0) continue // inflows aren't spending
    if (t.date < range.startISO || t.date > range.endISO) continue
    const key = t.categoryId ?? null
    totals.set(key, (totals.get(key) ?? 0) + Math.abs(t.amount))
  }
  const grandTotal = Array.from(totals.values()).reduce((a, b) => a + b, 0) || 1
  const rows: CategorySliceRow[] = []
  for (const [categoryId, total] of totals) {
    const meta =
      categoryId === null
        ? { name: 'Uncategorized', groupName: '—' }
        : (byId.get(categoryId) ?? lookupFallback(categoryId, groups))
    rows.push({
      categoryId,
      categoryName: meta.name,
      groupName: meta.groupName,
      total,
      pct: total / grandTotal,
    })
  }
  return rows.sort((a, b) => b.total - a.total)
}

function lookupFallback(categoryId: string, groups: CategoryGroup[]) {
  for (const g of groups) {
    const c = g.categories.find((c) => c.id === categoryId)
    if (c) return { name: c.name, groupName: g.name }
  }
  return { name: 'Unknown', groupName: '—' }
}

/**
 * Build a 12-month income vs expense series ending in `endMonth`.
 * Months with no transactions still appear with zeroes so the chart
 * shows a continuous timeline.
 */
export function incomeVsExpense(
  transactions: Transaction[],
  endYear: number,
  endMonth: number,
  monthCount = 12,
): MonthlyRow[] {
  const rows: MonthlyRow[] = []
  const byKey = new Map<string, MonthlyRow>()

  for (let i = monthCount - 1; i >= 0; i--) {
    const d = new Date(endYear, endMonth - 1 - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const row: MonthlyRow = {
      key,
      label: monthLabel.format(d),
      income: 0,
      expenses: 0,
      net: 0,
    }
    rows.push(row)
    byKey.set(key, row)
  }

  for (const t of transactions) {
    if (t.isTransfer) continue
    if (!t.date) continue
    // Parse the YYYY-MM directly from the ISO string. Going through
    // `new Date()` would convert to local time and put UTC-midnight
    // transactions in the wrong bucket for negative timezones.
    const match = /^(\d{4})-(\d{2})/.exec(t.date)
    if (!match) continue
    const key = `${match[1]}-${match[2]}`
    const row = byKey.get(key)
    if (!row) continue
    if (t.amount > 0) row.income += t.amount
    else row.expenses += Math.abs(t.amount)
  }
  for (const r of rows) r.net = r.income - r.expenses
  return rows
}

export interface DateRange {
  startISO: string
  endISO: string
  label: string
}

export function monthRange(year: number, month: number): DateRange {
  // ISO strings to match how iOS writes transaction.date.
  const start = new Date(year, month - 1, 1)
  const end = new Date(year, month, 0, 23, 59, 59, 999) // last day of month
  return {
    startISO: start.toISOString(),
    endISO: end.toISOString(),
    label: monthLabel.format(start),
  }
}
